/**
 * SEO helper: dynamically upserts document.title, meta description, Open Graph tags,
 * Twitter cards, canonical <link>, and (optionally) JSON-LD script tags.
 * Returns a cleanup fn so callers can strip everything on unmount.
 *
 *   const cleanup = applySEO({ title, description, image, url, jsonLd: [{...}] });
 *   useEffect(() => applySEO(...), [deps]);   // cleanup runs on re-render
 */
const MARK = "data-titan-seo";

function upsertMeta(selector, attrs) {
  let m = document.querySelector(selector);
  if (!m) {
    m = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== "content") m.setAttribute(k, v);
    });
    document.head.appendChild(m);
  }
  m.setAttribute("content", attrs.content || "");
  m.setAttribute(MARK, "1");
}

export function applySEO({
  title,
  description = "",
  image,
  url,
  type = "website",
  siteName = "The Titan Standard",
  twitterHandle = "@thetitanstandard",
  jsonLd = [],
  canonical,
}) {
  const prevTitle = document.title;
  if (title) document.title = title;

  const desc = (description || "").trim().slice(0, 300);
  const pageUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const canonicalHref = canonical || pageUrl;

  if (desc) upsertMeta('meta[name="description"]', { name: "description", content: desc });

  // Open Graph
  if (title) upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
  if (desc) upsertMeta('meta[property="og:description"]', { property: "og:description", content: desc });
  if (image) upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });
  if (pageUrl) upsertMeta('meta[property="og:url"]', { property: "og:url", content: pageUrl });
  upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
  upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: siteName });

  // Twitter cards
  upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: image ? "summary_large_image" : "summary" });
  if (twitterHandle) upsertMeta('meta[name="twitter:site"]', { name: "twitter:site", content: twitterHandle });
  if (title) upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
  if (desc) upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: desc });
  if (image) upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });

  // Canonical link — reuse ANY existing <link rel="canonical"> (whether we
  // created it or index.html shipped one) instead of appending a duplicate.
  // Duplicate canonicals are a common SEO bug that lets Google pick either
  // URL as the "true" one.
  if (canonicalHref) {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.setAttribute(MARK, "1");
    link.href = canonicalHref;
  }

  // JSON-LD blobs
  const injectedIds = [];
  (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).forEach((blob, i) => {
    if (!blob) return;
    const id = `titan-seo-ld-${i}`;
    document.getElementById(id)?.remove();
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = id;
    s.setAttribute(MARK, "1");
    s.text = JSON.stringify(blob);
    document.head.appendChild(s);
    injectedIds.push(id);
  });

  return () => {
    document.title = prevTitle;
    document.querySelectorAll(`[${MARK}]`).forEach((el) => el.remove());
    injectedIds.forEach((id) => document.getElementById(id)?.remove());
  };
}

/** Convenience: build a schema.org BreadcrumbList blob from crumb pairs. */
export function breadcrumbLD(crumbs) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url?.startsWith("http") ? c.url : `${origin}${c.url || "/"}`,
    })),
  };
}
